var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { compose } from "@ally/utilitarian";
import { AccountLOB, AccountStatus, AccountType, AccountTypeToLabel, } from "../types";
import { InvestAccountName, DepositAccountName, CCAccountName, } from "../../customization/types";
import { ownershipTypes, blockedLiveChatAlertCodesForDropdown, blockedProductSubTypes, blockedBankruptcyCodes, } from "./constants";
export var filterExpiredAccount = function (_a) {
    var closeDate = _a.closeDate;
    if (!closeDate) {
        return true;
    }
    var today = new Date();
    var dayDifference = today.getDate() - 60;
    var unixSixtyDaysAgo = today.setDate(dayDifference);
    var sixtyDaysAgo = new Date(unixSixtyDaysAgo).getTime();
    var isMoreThan60 = new Date(closeDate).getTime() < sixtyDaysAgo;
    return !isMoreThan60;
};
export var getByType = function () {
    var accTypes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        accTypes[_i] = arguments[_i];
    }
    return function (accounts) {
        return accounts.filter(function (_a) {
            var type = _a.type;
            var hasType = accTypes.includes(type);
            return hasType;
        });
    };
};
export var sortByAccountType = function (accountTypes) {
    return function (extraSort) {
        return function (a, b) {
            var aAcctType = a.type;
            var bAcctType = b.type;
            var aIndex = accountTypes.indexOf(aAcctType);
            var bIndex = accountTypes.indexOf(bAcctType);
            var order = aIndex - bIndex;
            if (extraSort && order === 0) {
                // istanbul ignore next
                return extraSort(a, b);
            }
            return order;
        };
    };
};
var determineDepositAccountBalance = function (_a) {
    var type = _a.type, balance = _a.balance;
    var amount = type === AccountType.CDA ? balance === null || balance === void 0 ? void 0 : balance.current : balance === null || balance === void 0 ? void 0 : balance.available;
    return amount !== null && amount !== void 0 ? amount : 0;
};
export var checkOwnershipType = function (_a) {
    var _b = _a.ownershipType, ownershipType = _b === void 0 ? "" : _b;
    return ownershipTypes.includes(ownershipType);
};
export var filterByRetirementIndicator = function (isRetirementAccount) {
    return function (account) {
        var _a;
        return !!((_a = account.indicator) === null || _a === void 0 ? void 0 : _a.retirement) === isRetirementAccount;
    };
};
export var sortByStatus = function (extraSort) {
    return function (a, b) {
        var getIsClosed = function (account) {
            return !!account.status.match(/closed/i);
        };
        var aIsClosed = getIsClosed(a);
        var bIsClosed = getIsClosed(b);
        if (aIsClosed && !bIsClosed) {
            // istanbul ignore next
            return 1;
        }
        if (!aIsClosed && bIsClosed) {
            // istanbul ignore next
            return -1;
        }
        if (extraSort) {
            return extraSort(a, b);
        }
        // istanbul ignore next
        return 0;
    };
};
var sortByOpenDate = function (extraSort) {
    return function (a, b) {
        var getOpenDate = function (_a) {
            var openDate = _a.openDate;
            // return 0 if open date is somehow undefined, this will sort the account to the bottom of a list
            return openDate ? new Date(openDate).getTime() : 0;
        };
        var aOpenDate = getOpenDate(a);
        var bOpenDate = getOpenDate(b);
        var order = aOpenDate - bOpenDate;
        if (extraSort && order === 0) {
            return extraSort(a, b);
        }
        return order;
    };
};
var sortByStatusAndOpenDate = compose(sortByStatus, sortByOpenDate)();
var getPlanType = function (acct) {
    var _a;
    return (((_a = acct.meta) === null || _a === void 0 ? void 0 : _a.retirementPlanType) || "").toUpperCase();
};
var sortByRetirementPlan = function (retirementPlans) {
    return function (extraSort) {
        return function (a, b) {
            var aRetPlan = getPlanType(a);
            var bRetPlan = getPlanType(b);
            var aIndex = retirementPlans.indexOf(aRetPlan);
            var bIndex = retirementPlans.indexOf(bRetPlan);
            var order = aIndex - bIndex;
            if (extraSort && order === 0) {
                return extraSort(a, b);
            }
            return order;
        };
    };
};
var getCustomDepositAccountOrder = function (customizationOpts) {
    var orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(function (_a) {
        var accountTypes = _a.accountTypes;
        accountTypes.forEach(function (_a) {
            var lob = _a.lob, name = _a.name, order = _a.order;
            if (lob === "DEPOSIT" && name in DepositAccountName) {
                orderedAccounts.push({ name: name, order: order });
            }
        });
    });
    orderedAccounts.sort(function (a, b) { return a.order - b.order; });
    return orderedAccounts;
};
var sortDepositAccounts = function (accountsToSort, customizationOpts) {
    getCustomDepositAccountOrder(customizationOpts).forEach(function (_a) {
        var name = _a.name, order = _a.order;
        accountsToSort[name].order = order;
    });
    /**
     * Accounts without an order value are required to be placed at the front
     * of the array in their default order. Hence the `|| 0` to give `undefined`
     * order values a lower value in the sort order than defined values, which
     * are indexed from 1.
     */
    return Object.values(accountsToSort)
        .sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
        .map(function (_a) {
        var acct = _a.acct;
        return acct;
    });
};
/** These utilities format data to make it consumable for accounts dropdown */
var mapDepositAccountsForDropdown = function (account) {
    var balanceNumber = determineDepositAccountBalance({
        type: account.type,
        balance: account.balance,
    });
    return __assign(__assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: balanceNumber });
};
export var formatDepositAccountsForDropdown = function (ddaAccts, mmaAccts, sdaAccts, cmgAccts, cdaAccts, retAccts, customizationOpts) {
    var _a;
    // CHECKING ACCOUNTS
    var interestCheckingGroup = (ddaAccts || [])
        .filter(filterExpiredAccount)
        .sort(sortByStatusAndOpenDate);
    // SAVINGS ACCOUNTS
    var otherDepAccountsGroup = __spreadArray(__spreadArray(__spreadArray([], __read((mmaAccts || [])), false), __read((sdaAccts || [])), false), __read((cmgAccts || [])), false).filter(filterExpiredAccount)
        .sort(compose(sortByStatus, sortByAccountType([AccountType.MMA, AccountType.SDA, AccountType.CMG]), sortByOpenDate)());
    // CD ACCOUNTS
    var cdGroup = (cdaAccts || [])
        .filter(filterExpiredAccount)
        .sort(sortByStatusAndOpenDate);
    // IRA ACCOUNTS
    var retirementGroup = (retAccts || [])
        .filter(filterExpiredAccount)
        .sort(compose(sortByRetirementPlan(["SEP", "ROTH", "TRADITIONAL"]), sortByStatus, sortByAccountType([AccountType.SDA, AccountType.CDA]), sortByOpenDate)());
    var sortedAccounts = sortDepositAccounts((_a = {},
        _a[DepositAccountName.CHECKING] = { acct: interestCheckingGroup },
        _a[DepositAccountName.SAVINGS] = { acct: otherDepAccountsGroup },
        _a[DepositAccountName.CD] = { acct: cdGroup },
        _a[DepositAccountName.IRA] = { acct: retirementGroup },
        _a), customizationOpts);
    return sortedAccounts.flat().map(mapDepositAccountsForDropdown);
};
export var mapInvestAccountsForDropdown = function (account) {
    var _a, _b;
    return __assign(__assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.value) === null || _a === void 0 ? void 0 : _a.total) !== null && _b !== void 0 ? _b : 0 });
};
var getCustomInvestAccountOrder = function (customizationOpts) {
    var orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(function (_a) {
        var accountTypes = _a.accountTypes;
        accountTypes.forEach(function (_a) {
            var lob = _a.lob, name = _a.name, order = _a.order;
            if (lob === "INVEST" && name in InvestAccountName) {
                orderedAccounts.push({ name: name, order: order });
            }
        });
    });
    orderedAccounts.sort(function (a, b) { return a.order - b.order; });
    return orderedAccounts;
};
export var formatInvestAccountsForDropdown = function (accounts, customizationOpts) {
    var accountOrder = getCustomInvestAccountOrder(customizationOpts);
    var orderedAccountNames = [
        // INVW - Wealth Portfolio
        AccountType.INVW,
        // INVA - Robo Portfolio
        AccountType.INVA,
        // INVS - Self-directed
        AccountType.INVS,
    ].sort(function (a, b) {
        var _a, _b;
        return (((_a = accountOrder.find(function (acct) { return acct.name === a.toString(); })) === null || _a === void 0 ? void 0 : _a.order) || 0) -
            (((_b = accountOrder.find(function (acct) { return acct.name === b.toString(); })) === null || _b === void 0 ? void 0 : _b.order) || 0);
    });
    var investGroup = accounts.sort(compose(sortByAccountType(orderedAccountNames), sortByStatus)());
    return investGroup.map(mapInvestAccountsForDropdown);
};
export var mapMortgageAccountsForDropdown = function (account) {
    var _a, _b;
    return __assign(__assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.amountDue) !== null && _b !== void 0 ? _b : 0 });
};
export var sortByAccountNumber = function (a, b) {
    return parseInt(a.accountNumber, 10) - parseInt(b.accountNumber, 10);
};
export var formatMortgageAccountsForDropdown = function (accounts) {
    var isMortgage = function (a) { var _a; return a.type === AccountType.MORTGAGE && !((_a = a.indicator) === null || _a === void 0 ? void 0 : _a.blocked); };
    var isHeloc = function (a) { var _a; return a.type === AccountType.HOME_EQUITY && !((_a = a.indicator) === null || _a === void 0 ? void 0 : _a.blocked); };
    var isActive = function (a) { return a.status === AccountStatus.ACTIVE; };
    var isPaidOff = function (a) { return a.status === AccountStatus.PAIDOFF; };
    var activeMortgageAccounts = accounts.filter(function (account) { return isMortgage(account) && isActive(account); });
    var paidOffMortgageAccounts = accounts.filter(function (account) { return isMortgage(account) && isPaidOff(account); });
    var activeHelocAccounts = accounts.filter(function (account) { return isHeloc(account) && isActive(account); });
    var paidOffHelocAccounts = accounts.filter(function (account) { return isHeloc(account) && isPaidOff(account); });
    var mortgageGroup = [
        activeMortgageAccounts,
        paidOffMortgageAccounts,
        activeHelocAccounts,
        paidOffHelocAccounts,
    ]
        .map(function (accts) { return accts.sort(sortByAccountNumber); })
        .flat();
    return mortgageGroup.map(mapMortgageAccountsForDropdown);
};
function mapCreditCardAccountsForDropdown(account) {
    var _a, _b;
    return __assign(__assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.paymentDue) !== null && _b !== void 0 ? _b : 0 });
}
var getCustomCreditCardAccountOrder = function (customizationOpts) {
    var orderedAccounts = [];
    customizationOpts === null || customizationOpts === void 0 ? void 0 : customizationOpts.forEach(function (_a) {
        var accountTypes = _a.accountTypes;
        accountTypes.forEach(function (_a) {
            var lob = _a.lob, name = _a.name, order = _a.order;
            if (lob === "CC" && name in CCAccountName) {
                orderedAccounts.push({ name: name, order: order });
            }
        });
    });
    orderedAccounts.sort(function (a, b) { return a.order - b.order; });
    return orderedAccounts;
};
export var formatCreditCardAccountsForDropdown = function (accounts, customizationOptions) {
    var accountOrder = getCustomCreditCardAccountOrder(customizationOptions);
    var orderedAccountNames = [
        AccountType.PLATINUM,
        AccountType.EVERYDAY,
        AccountType.UNLIMITED,
    ].sort(function (a, b) {
        var _a, _b;
        return (((_a = accountOrder.find(function (acct) { return acct.name === a.toString(); })) === null || _a === void 0 ? void 0 : _a.order) || 0) -
            (((_b = accountOrder.find(function (acct) { return acct.name === b.toString(); })) === null || _b === void 0 ? void 0 : _b.order) || 0);
    });
    var creditCardAccounts = accounts.sort(compose(sortByAccountType(orderedAccountNames)
    // sortByStatus // TODO may need this if we show closed CC accounts
    )());
    return creditCardAccounts.map(mapCreditCardAccountsForDropdown);
};
var isAllowedPaidOffAccount = function (a) {
    var _a, _b;
    var cutOffDayInMs = new Date(Date.now() - 45 * 24 * 60 * 60 * 1000);
    if ((_a = a.meta) === null || _a === void 0 ? void 0 : _a.paidInFullOrZeroBalance) {
        var isLessThanFortyFive = new Date((_b = a.meta) === null || _b === void 0 ? void 0 : _b.paidInFullDate) >= cutOffDayInMs;
        // Only show paid in full account if paid in full in the last 45 days
        return isLessThanFortyFive;
    }
    return true;
};
export var filterAutoAccountsForDashboard = function (accounts, isBankruptcyAccepted) {
    var isAllowedBankruptcyAccount = function (a, hasBankruptcyAccepted) {
        var _a, _b;
        if (hasBankruptcyAccepted)
            return true;
        return !((_b = (_a = a.meta) === null || _a === void 0 ? void 0 : _a.bankruptcyCodes) === null || _b === void 0 ? void 0 : _b.some(function (bankruptcyCode) {
            return blockedBankruptcyCodes.includes(bankruptcyCode);
        }));
    };
    var autoAccountsForDashboard = accounts.filter(function (account) {
        return isAllowedBankruptcyAccount(account, isBankruptcyAccepted) &&
            isAllowedPaidOffAccount(account);
    });
    return autoAccountsForDashboard;
};
export var mapAutoAccountsForDropdown = function (account) {
    var _a, _b;
    return __assign(__assign({}, account), { nickName: account.nickName || account.name, accountTypeLabel: AccountTypeToLabel[account.type], accountBalanceToShow: (_b = (_a = account.meta) === null || _a === void 0 ? void 0 : _a.nextPayment) === null || _b === void 0 ? void 0 : _b.totalAmountDue });
};
export var formatAutoAccountsForDropdown = function (accounts) {
    var isAuto = function (a) { return a.lob === AccountLOB.AUTO; };
    var isAllowedLiveChatAlertCodes = function (a) {
        var _a, _b;
        return (_b = (_a = a.meta) === null || _a === void 0 ? void 0 : _a.liveChatAlertCodes) === null || _b === void 0 ? void 0 : _b.every(function (code) { return !blockedLiveChatAlertCodesForDropdown.includes(code); });
    };
    var isAllowedSubType = function (a) {
        var _a;
        if (!blockedProductSubTypes.includes((_a = a.meta) === null || _a === void 0 ? void 0 : _a.productSubtype)) {
            return true;
        }
        return false;
    };
    var autoAccountsForDropdown = accounts.filter(function (account) {
        return isAuto(account) &&
            isAllowedLiveChatAlertCodes(account) &&
            isAllowedSubType(account) &&
            isAllowedPaidOffAccount(account);
    });
    return autoAccountsForDropdown.map(mapAutoAccountsForDropdown);
};
//# sourceMappingURL=utils.js.map